import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Building2, Clock, DollarSign, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { venueRatesService, VenueRate } from '@/services/venueRatesService';

interface VenueProfileSetupProps {
  venueId: string;
}

interface VenueProfileData {
  venue_name: string;
  venue_address: string;
  venue_description: string;
  operating_hours_start: string;
  operating_hours_end: string;
  operating_days: string[];
}

interface RateFormData {
  facility_type: string;
  hourly_rate: number;
  daily_rate?: number;
  peak_hour_multiplier: number;
}

const DAYS_OF_WEEK = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

export const VenueProfileSetup = ({ venueId }: VenueProfileSetupProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [rates, setRates] = useState<VenueRate[]>([]);
  const [showRateForm, setShowRateForm] = useState(false);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<VenueProfileData>({
    defaultValues: {
      operating_hours_start: '06:00',
      operating_hours_end: '22:00',
      operating_days: DAYS_OF_WEEK
    }
  });

  const { register: registerRate, handleSubmit: handleRateSubmit, reset: resetRate, formState: { errors: rateErrors } } = useForm<RateFormData>({
    defaultValues: {
      peak_hour_multiplier: 1.0
    }
  });

  const selectedDays = watch('operating_days') || [];

  useEffect(() => {
    loadVenueProfile();
    loadVenueRates();
  }, [venueId]);

  const loadVenueProfile = async () => {
    try {
      console.log('🏟️ VENUE PROFILE - Loading profile for venue ID:', venueId);
      
      // Try to load from profiles table first (for Supabase users)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('venue_name, venue_address, venue_description, operating_hours_start, operating_hours_end, operating_days')
        .eq('id', venueId)
        .single();
      
      if (profileError) {
        console.error('🏟️ VENUE PROFILE - Error from profiles table:', profileError);
        // If no data found, that's okay - they haven't set up their profile yet
        if (profileError.code === 'PGRST116') {
          console.log('🏟️ VENUE PROFILE - No profile found, using defaults');
        } else {
          throw profileError;
        }
      } else {
        console.log('🏟️ VENUE PROFILE - Loaded from profiles:', profileData);
        if (profileData) {
          setValue('venue_name', profileData.venue_name || '');
          setValue('venue_address', profileData.venue_address || '');
          setValue('venue_description', profileData.venue_description || '');
          setValue('operating_hours_start', profileData.operating_hours_start || '06:00');
          setValue('operating_hours_end', profileData.operating_hours_end || '22:00');
          setValue('operating_days', profileData.operating_days || DAYS_OF_WEEK);
        }
      }
    } catch (error) {
      console.error('🏟️ VENUE PROFILE - Error loading venue profile:', error);
      toast.error('Failed to load venue profile');
    } finally {
      setIsLoading(false);
    }
  };

  const loadVenueRates = async () => {
    const venueRates = await venueRatesService.getVenueRates(venueId);
    setRates(venueRates);
  };

  const onSubmitProfile = async (data: VenueProfileData) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          venue_name: data.venue_name,
          venue_address: data.venue_address,
          venue_description: data.venue_description,
          operating_hours_start: data.operating_hours_start,
          operating_hours_end: data.operating_hours_end,
          operating_days: data.operating_days
        })
        .eq('id', venueId);

      if (error) throw error;

      toast.success('Venue profile updated successfully');
    } catch (error) {
      console.error('Error updating venue profile:', error);
      toast.error('Failed to update venue profile');
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmitRate = async (data: RateFormData) => {
    const newRate = await venueRatesService.createVenueRate(venueId, data);
    if (newRate) {
      setRates([...rates, newRate]);
      resetRate();
      setShowRateForm(false);
      toast.success('Rate added successfully');
    }
  };

  const deleteRate = async (rateId: string) => {
    const success = await venueRatesService.deleteVenueRate(rateId);
    if (success) {
      setRates(rates.filter(r => r.id !== rateId));
      toast.success('Rate deleted successfully');
    }
  };

  const toggleDay = (day: string) => {
    const currentDays = selectedDays;
    const newDays = currentDays.includes(day) 
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    setValue('operating_days', newDays);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Venue Profile */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            Venue Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit(onSubmitProfile)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="venue_name" className="text-white">Venue Name *</Label>
                <Input
                  id="venue_name"
                  {...register('venue_name', { required: 'Venue name is required' })}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Central Sports Complex"
                />
                {errors.venue_name && (
                  <p className="text-red-400 text-sm">{errors.venue_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue_address" className="text-white">Address</Label>
                <Input
                  id="venue_address"
                  {...register('venue_address')}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="123 Sports Ave, City, State"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue_description" className="text-white">Description</Label>
              <Textarea
                id="venue_description"
                {...register('venue_description')}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Describe your venue facilities and amenities..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="operating_hours_start" className="text-white">Opening Time</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="operating_hours_start"
                    type="time"
                    {...register('operating_hours_start', { required: 'Opening time is required' })}
                    className="bg-gray-800 border-gray-700 text-white pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="operating_hours_end" className="text-white">Closing Time</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="operating_hours_end"
                    type="time"
                    {...register('operating_hours_end', { required: 'Closing time is required' })}
                    className="bg-gray-800 border-gray-700 text-white pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Operating Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <Badge
                    key={day}
                    variant={selectedDays.includes(day) ? "default" : "outline"}
                    className={`cursor-pointer ${
                      selectedDays.includes(day) 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-800 text-gray-300 border-gray-600'
                    }`}
                    onClick={() => toggleDay(day)}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={isSaving}
                className="bg-primary hover:bg-primary/80 text-black"
              >
                {isSaving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Venue Rates */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-primary" />
              Pricing Rates
            </CardTitle>
            <Button
              onClick={() => setShowRateForm(!showRateForm)}
              className="bg-primary hover:bg-primary/80 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rate
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {showRateForm && (
            <Card className="bg-gray-800 border-gray-700 mb-6">
              <CardContent className="p-4">
                <form onSubmit={handleRateSubmit(onSubmitRate)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Facility Type</Label>
                      <Input
                        {...registerRate('facility_type', { required: 'Facility type is required' })}
                        className="bg-gray-900 border-gray-600 text-white"
                        placeholder="Field A, Court 1, etc."
                      />
                      {rateErrors.facility_type && (
                        <p className="text-red-400 text-sm">{rateErrors.facility_type.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Hourly Rate ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        {...registerRate('hourly_rate', { 
                          required: 'Hourly rate is required',
                          valueAsNumber: true,
                          min: 0
                        })}
                        className="bg-gray-900 border-gray-600 text-white"
                        placeholder="50.00"
                      />
                      {rateErrors.hourly_rate && (
                        <p className="text-red-400 text-sm">{rateErrors.hourly_rate.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Daily Rate ($) <span className="text-gray-400">(Optional)</span></Label>
                      <Input
                        type="number"
                        step="0.01"
                        {...registerRate('daily_rate', { valueAsNumber: true, min: 0 })}
                        className="bg-gray-900 border-gray-600 text-white"
                        placeholder="300.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Peak Hour Multiplier</Label>
                      <Input
                        type="number"
                        step="0.1"
                        {...registerRate('peak_hour_multiplier', { 
                          valueAsNumber: true,
                          min: 0.5,
                          max: 3.0
                        })}
                        className="bg-gray-900 border-gray-600 text-white"
                        placeholder="1.5"
                      />
                      <p className="text-sm text-gray-400">
                        1.0 = normal rate, 1.5 = 50% increase for peak hours
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowRateForm(false)}
                      className="border-gray-600 text-gray-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-primary hover:bg-primary/80 text-black"
                    >
                      Add Rate
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="space-y-4">
            {rates.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                No rates configured yet. Add your first rate to enable revenue tracking.
              </div>
            ) : (
              rates.map(rate => (
                <div key={rate.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <h4 className="text-white font-semibold">{rate.facility_type}</h4>
                    <div className="text-sm text-gray-400">
                      <span>${rate.hourly_rate}/hour</span>
                      {rate.daily_rate && <span> • ${rate.daily_rate}/day</span>}
                      {rate.peak_hour_multiplier !== 1.0 && (
                        <span> • {rate.peak_hour_multiplier}x peak</span>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => deleteRate(rate.id)}
                    variant="outline"
                    size="sm"
                    className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};